import React from 'react';
import { FormField, FormFieldType, Student } from '../../types';
import { responseKey } from '../../pages/formFieldKey';

interface FieldRendererProps {
  field: FormField;
  value: any;
  onChange: (value: any) => void;
  error?: string;
  disabled?: boolean;
  students?: Student[];
  /**
   * Other CONTACT_LIST fields on the same form, with their current values.
   * Passed straight through to `ContactListInput` so it can offer "add from
   * [other list]" — the parent shouldn't have to retype the same grandmother
   * who's already in the emergency-contacts section.
   */
  otherContactLists?: Array<{ fieldId: string; label: string; contacts: ContactRow[] }>;
}

const FieldRenderer: React.FC<FieldRendererProps> = ({
  field,
  value,
  onChange,
  error,
  disabled = false,
  students = [],
  otherContactLists = [],
}) => {
  const baseInputClass = `block w-full rounded-md shadow-sm sm:text-sm ${
    error
      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
      : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
  } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`;

  // Every rendered control gets a stable, unique testid — this renderer is the
  // only source of testids for dynamic form fields, and CLAUDE.md requires a
  // data-testid on every interactive element with getByTestId in specs.
  // Keyed the same way the response value is (field.key ?? field.id), so the
  // testid matches the key the answer is stored under.
  const testId = `form-field-${field.key ?? field.id}`;

  const renderField = () => {
    switch (field.type) {
      case FormFieldType.STUDENT_SELECTOR:
        return (
          <select
            data-testid={testId}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            className={baseInputClass}
          >
            <option value="">Select a student...</option>
            {students.map((student) => (
              <option key={student.id} value={student.id}>
                {student.firstName} {student.lastName}
              </option>
            ))}
          </select>
        );

      case FormFieldType.TEXT:
        return (
          <input
            data-testid={testId}
            type="text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            disabled={disabled}
            className={baseInputClass}
            minLength={field.validation?.minLength}
            maxLength={field.validation?.maxLength}
            pattern={field.validation?.pattern}
          />
        );

      case FormFieldType.TEXTAREA:
        return (
          <textarea
            data-testid={testId}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            disabled={disabled}
            rows={4}
            className={baseInputClass}
            minLength={field.validation?.minLength}
            maxLength={field.validation?.maxLength}
          />
        );

      case FormFieldType.CHECKBOX:
        return (
          <div className="flex items-center">
            <input
              data-testid={testId}
              type="checkbox"
              checked={value || false}
              onChange={(e) => onChange(e.target.checked)}
              disabled={disabled}
              className={`h-4 w-4 rounded focus:ring-primary-500 ${
                error ? 'border-red-300 text-red-600' : 'border-gray-300 text-primary-600'
              } ${disabled ? 'cursor-not-allowed' : ''}`}
            />
            {field.helpText && <span className="ml-2 text-sm text-gray-500">{field.helpText}</span>}
          </div>
        );

      case FormFieldType.CHECKBOX_GROUP:
        return (
          <div className="space-y-2">
            {field.options?.map((option) => (
              <div key={option} className="flex items-center">
                <input
                  data-testid={`${testId}-${option}`}
                  type="checkbox"
                  checked={(value || []).includes(option)}
                  onChange={(e) => {
                    const currentValues = value || [];
                    if (e.target.checked) {
                      onChange([...currentValues, option]);
                    } else {
                      onChange(currentValues.filter((v: string) => v !== option));
                    }
                  }}
                  disabled={disabled}
                  className={`h-4 w-4 rounded focus:ring-primary-500 ${
                    error ? 'border-red-300 text-red-600' : 'border-gray-300 text-primary-600'
                  } ${disabled ? 'cursor-not-allowed' : ''}`}
                />
                <label className="ml-2 text-sm text-gray-700">{option}</label>
              </div>
            ))}
          </div>
        );

      case FormFieldType.RADIO:
        return (
          <div className="space-y-2">
            {field.options?.map((option) => (
              <div key={option} className="flex items-center">
                <input
                  data-testid={`${testId}-${option}`}
                  type="radio"
                  name={field.id}
                  checked={value === option}
                  onChange={() => onChange(option)}
                  disabled={disabled}
                  className={`h-4 w-4 focus:ring-primary-500 ${
                    error ? 'border-red-300 text-red-600' : 'border-gray-300 text-primary-600'
                  } ${disabled ? 'cursor-not-allowed' : ''}`}
                />
                <label className="ml-2 text-sm text-gray-700">{option}</label>
              </div>
            ))}
          </div>
        );

      case FormFieldType.SELECT:
        return (
          <select
            data-testid={testId}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            className={baseInputClass}
          >
            <option value="">{field.placeholder || 'Select an option...'}</option>
            {field.options?.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case FormFieldType.DATE:
        return (
          <input
            data-testid={testId}
            type="date"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            className={baseInputClass}
          />
        );

      case FormFieldType.TIME:
        return (
          <input
            data-testid={testId}
            type="time"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            className={baseInputClass}
          />
        );

      case FormFieldType.NUMBER:
        return (
          <input
            data-testid={testId}
            type="number"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
            placeholder={field.placeholder}
            disabled={disabled}
            className={baseInputClass}
            min={field.validation?.min}
            max={field.validation?.max}
          />
        );

      case FormFieldType.SIGNATURE:
        return (
          <div className="space-y-2">
            <input
              data-testid={testId}
              type="text"
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              placeholder="Type your full name to sign"
              disabled={disabled}
              className={`${baseInputClass} font-signature italic`}
            />
            {value && (
              <div className="p-3 bg-gray-50 rounded-md border border-gray-200">
                <p className="text-xs text-gray-500 mb-1">Signature Preview:</p>
                <p className="text-lg font-signature italic text-gray-800">{value}</p>
              </div>
            )}
          </div>
        );

      case FormFieldType.CONTACT_LIST: {
        const sources = otherContactLists.filter(
          (cl) => cl.fieldId !== field.id && cl.contacts.length > 0
        );
        return (
          <ContactListInput
            value={value}
            onChange={onChange}
            disabled={disabled}
            sources={sources}
          />
        );
      }

      case FormFieldType.FILE:
        return (
          <div className="space-y-2">
            <input
              data-testid={testId}
              type="file"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  onChange({
                    file,
                    fileName: file.name,
                    fileType: file.type,
                    fileSize: file.size,
                  });
                }
              }}
              disabled={disabled}
              className={`block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 ${
                disabled ? 'cursor-not-allowed' : ''
              }`}
              accept={field.validation?.allowedFileTypes?.join(',')}
            />
            {value?.fileName && (
              <p className="text-sm text-gray-600">
                Selected: {value.fileName} ({Math.round(value.fileSize / 1024)} KB)
              </p>
            )}
            {field.validation?.maxFileSize && (
              <p className="text-xs text-gray-500">
                Max file size: {Math.round(field.validation.maxFileSize / (1024 * 1024))} MB
              </p>
            )}
          </div>
        );

      default:
        return <p className="text-sm text-gray-500">Unknown field type: {field.type}</p>;
    }
  };

  // Use the same response key the value binds to (field.key, or field.id for
  // legacy null-key fields) so each field's testid is unique — otherwise every
  // null-key field would be `field-undefined`.
  const rk = responseKey(field);
  return (
    <div className="space-y-1" data-testid={`field-${rk}`}>
      <label className="block text-sm font-medium text-gray-700">
        {field.label}
        {field.required && (
          <span className="text-red-500 ml-1" data-testid={`field-${rk}-required-marker`}>
            *
          </span>
        )}
      </label>
      {renderField()}
      {field.helpText && field.type !== FormFieldType.CHECKBOX && (
        <p className="text-sm text-gray-500">{field.helpText}</p>
      )}
      {error && (
        <p className="text-sm text-red-600" data-testid={`field-${rk}-error`}>
          {error}
        </p>
      )}
    </div>
  );
};

export default FieldRenderer;

/**
 * Repeating-group input for the CONTACT_LIST field type. On REGISTRATION
 * forms this feeds the student's pickup-contact list — each entry becomes
 * a PickupContact row on submit.
 *
 * Wire shape (what we store under `responses[field.id]`):
 *   Array<{ id, name, relationship, relationshipType, phone }>
 *
 * Each entry gets a stable client-side id so add/remove doesn't shuffle
 * keys and lose focus. Empty rows (no name or no relationship) are
 * silently dropped server-side by extractPickupContacts — so the parent
 * can leave a half-filled row in the form without breaking the submit.
 */
interface ContactRow {
  id: string;
  name: string;
  relationship: string;
  relationshipType: string;
  phone: string;
}

const RELATIONSHIP_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'PARENT', label: 'Parent' },
  { value: 'GUARDIAN', label: 'Guardian' },
  { value: 'GRANDPARENT', label: 'Grandparent' },
  { value: 'AUNT_UNCLE', label: 'Aunt / Uncle' },
  { value: 'SIBLING', label: 'Sibling (16+)' },
  { value: 'FAMILY_FRIEND', label: 'Family friend' },
  { value: 'NEIGHBOUR', label: 'Neighbour' },
  { value: 'OTHER', label: 'Other' },
];

const ContactListInput: React.FC<{
  value: any;
  onChange: (v: ContactRow[]) => void;
  disabled?: boolean;
  /** Other CONTACT_LIST fields' current entries, offered as "add from …". */
  sources?: Array<{ fieldId: string; label: string; contacts: ContactRow[] }>;
}> = ({ value, onChange, disabled, sources = [] }) => {
  // Hydrate the incoming value. Stored rows may not have a client id (server
  // doesn't return one on read); generate one so list keys stay stable.
  const rows: ContactRow[] = Array.isArray(value)
    ? value.map((r: any, idx: number) => ({
        id: r.id ?? `row-${idx}`,
        name: r.name ?? '',
        relationship: r.relationship ?? '',
        relationshipType: r.relationshipType ?? 'OTHER',
        phone: r.phone ?? '',
      }))
    : [];

  const updateRow = (id: string, patch: Partial<ContactRow>) => {
    onChange(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };
  const removeRow = (id: string) => {
    onChange(rows.filter((r) => r.id !== id));
  };
  const addRow = () => {
    onChange([
      ...rows,
      {
        id: `row-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        name: '',
        relationship: '',
        relationshipType: 'OTHER',
        phone: '',
      },
    ]);
  };

  // "Already in current list" = same name+phone (case/whitespace-insensitive
  // on name). Prevents an accidental double-add when the parent clicks the
  // same suggestion twice. We keep phone in the key because two people may
  // legitimately share a name in a household.
  const norm = (s: string) => s.trim().toLowerCase();
  const currentKeys = new Set(rows.map((r) => `${norm(r.name)}|${r.phone.trim()}`));

  const copyFromSource = (src: ContactRow) => {
    const key = `${norm(src.name)}|${src.phone.trim()}`;
    if (currentKeys.has(key)) return;
    onChange([
      ...rows,
      {
        id: `row-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        name: src.name,
        relationship: src.relationship,
        relationshipType: src.relationshipType || 'OTHER',
        phone: src.phone,
      },
    ]);
  };

  return (
    <div className="space-y-3" data-testid="contact-list-input">
      {rows.length === 0 && <p className="text-sm text-gray-500 italic">No contacts added yet.</p>}
      {rows.map((row, idx) => (
        <div
          key={row.id}
          data-testid={`contact-row-${idx}`}
          className="border border-gray-200 rounded-md p-3 space-y-2 bg-gray-50"
        >
          <div className="flex items-baseline justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Contact {idx + 1}
            </span>
            <button
              type="button"
              onClick={() => removeRow(row.id)}
              disabled={disabled}
              data-testid={`contact-row-${idx}-remove`}
              className="text-xs text-red-600 hover:text-red-700 disabled:opacity-50"
            >
              Remove
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-700">Full name</label>
              <input
                type="text"
                value={row.name}
                onChange={(e) => updateRow(row.id, { name: e.target.value })}
                disabled={disabled}
                placeholder="e.g. Mary Ryan"
                data-testid={`contact-row-${idx}-name`}
                className="mt-0.5 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700">Relationship</label>
              <select
                value={row.relationshipType}
                onChange={(e) => updateRow(row.id, { relationshipType: e.target.value })}
                disabled={disabled}
                data-testid={`contact-row-${idx}-relationshipType`}
                className="mt-0.5 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              >
                {RELATIONSHIP_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700">
                Description <span className="text-gray-400 font-normal">(short)</span>
              </label>
              <input
                type="text"
                value={row.relationship}
                onChange={(e) => updateRow(row.id, { relationship: e.target.value })}
                disabled={disabled}
                placeholder="e.g. Grandmother, Mary"
                data-testid={`contact-row-${idx}-relationship`}
                className="mt-0.5 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700">
                Phone <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                type="tel"
                value={row.phone}
                onChange={(e) => updateRow(row.id, { phone: e.target.value })}
                disabled={disabled}
                placeholder="087 123 4567"
                data-testid={`contact-row-${idx}-phone`}
                className="mt-0.5 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
          </div>
        </div>
      ))}
      {sources.map((src) => {
        const eligible = src.contacts.filter((c) => c.name.trim().length > 0);
        if (eligible.length === 0) return null;
        return (
          <div
            key={src.fieldId}
            data-testid={`contact-list-source-${src.fieldId}`}
            className="rounded-md border border-gray-200 bg-white p-3"
          >
            <p className="text-xs font-medium text-gray-700">
              Add from <span className="text-gray-900">{src.label}</span>
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {eligible.map((c, i) => {
                const key = `${norm(c.name)}|${c.phone.trim()}`;
                const alreadyAdded = currentKeys.has(key);
                return (
                  <button
                    key={`${src.fieldId}-${i}`}
                    type="button"
                    onClick={() => copyFromSource(c)}
                    disabled={disabled || alreadyAdded}
                    data-testid={`contact-list-source-${src.fieldId}-add-${i}`}
                    className="inline-flex items-center gap-1 rounded-full border border-gray-300 bg-gray-50 px-2.5 py-1 text-xs text-gray-700 hover:bg-primary-50 hover:border-primary-300 hover:text-primary-700 disabled:opacity-50 disabled:hover:bg-gray-50 disabled:hover:border-gray-300 disabled:hover:text-gray-700 disabled:cursor-not-allowed"
                  >
                    + {c.name || 'Unnamed contact'}
                    {alreadyAdded && <span className="ml-1 text-gray-400">added</span>}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
      <button
        type="button"
        onClick={addRow}
        disabled={disabled}
        data-testid="contact-list-add"
        className="w-full p-2.5 rounded-md border border-dashed border-gray-300 text-sm font-medium text-gray-600 hover:bg-white hover:border-primary-400 hover:text-primary-700 transition disabled:opacity-50"
      >
        + Add a contact
      </button>
    </div>
  );
};
